/**
 * Pricing Plans Constants
 * Defines all subscription plan configurations
 */

export type PlanTier = "core" | "plus" | "premium";
export type BillingPeriod = "monthly" | "yearly";

export interface PlanFeature {
	text: string;
	included: boolean;
}

export interface PlanConfig {
	id: PlanTier;
	nameKey: string; // i18n key
	features: string[]; // i18n keys
	isPopular?: boolean;
	entitlementId?: string; // RevenueCat entitlement identifier
}

/**
 * Plan configurations
 * Features are defined as i18n keys for multi-language support
 */
export const PLAN_CONFIGS: Record<PlanTier, PlanConfig> = {
	core: {
		id: "core",
		nameKey: "plans.core.name",
		features: [
			"features.aiCompanionBasic",
			"features.limitedConsults",
			"features.basicAnalytics",
			"features.standardSupport",
			"features.premiumMarketplace",
			"features.conciergeManager",
		],
		isPopular: false,
	},
	plus: {
		id: "plus",
		nameKey: "plans.plus.name",
		features: [
			"features.aiCompanionFull",
			"features.unlimitedConsults",
			"features.advancedAnalytics",
			"features.fastTrackSupport",
			"features.premiumMarketplace",
			"features.conciergeManager",
		],
		isPopular: true,
		entitlementId: "plus", // Must match RevenueCat entitlement identifier
	},
	premium: {
		id: "premium",
		nameKey: "plans.premium.name",
		features: [
			"features.aiCompanionFull",
			"features.unlimitedConsults",
			"features.advancedAnalytics",
			"features.fastTrackSupport",
			"features.premiumMarketplace",
			"features.conciergeManager",
		],
		isPopular: false,
		entitlementId: "premium", // Must match RevenueCat entitlement identifier
	},
};

/**
 * Feature availability matrix
 * Maps features to which plans include them
 */
export const PLAN_FEATURES: Record<PlanTier, PlanFeature[]> = {
	core: [
		{ text: "features.aiCompanionBasic", included: true },
		{ text: "features.limitedConsults", included: true },
		{ text: "features.basicAnalytics", included: true },
		{ text: "features.standardSupport", included: true },
		{ text: "features.premiumMarketplace", included: false },
		{ text: "features.conciergeManager", included: false },
	],
	plus: [
		{ text: "features.aiCompanionFull", included: true },
		{ text: "features.unlimitedConsults", included: true },
		{ text: "features.advancedAnalytics", included: true },
		{ text: "features.fastTrackSupport", included: true },
		{ text: "features.premiumMarketplace", included: true },
		{ text: "features.conciergeManager", included: true },
	],
	premium: [
		{ text: "features.aiCompanionFull", included: true },
		{ text: "features.unlimitedConsults", included: true },
		{ text: "features.advancedAnalytics", included: true },
		{ text: "features.fastTrackSupport", included: true },
		{ text: "features.premiumMarketplace", included: true },
		{ text: "features.conciergeManager", included: true },
	],
};

/**
 * Plan order for display
 */
export const PLAN_ORDER: PlanTier[] = ["core", "plus", "premium"];

/**
 * RevenueCat offering identifiers
 * Maps plan tiers to RevenueCat offering names
 */
export const REVENUECAT_OFFERING_IDS: Record<
	PlanTier,
	{ monthly: string | null; annual: string | null }
> = {
	core: { monthly: null, annual: null }, // Free plan, no offering
	plus: { monthly: "web-offering", annual: "plus_annual" },
	premium: { monthly: "web_premium", annual: "premium_annual" },
};
